<script setup lang="ts">
import { isVNode, type Component, type VNode } from 'vue';
import type { RouteLocationRaw } from 'vue-router';
import TmlAlertBox from './TmlAlertBox.vue';
import TmlButton from './TmlButton.vue';

type ErrorMessage = string | VNode | Component;

const renderMessage = (message: Exclude<ErrorMessage, string>) =>
  isVNode(message) ? { render: () => message } : message;

defineProps<{
  error: {
    message: ErrorMessage;
    messages?: ErrorMessage[];
    level: 'danger' | 'warning' | 'success';
    actions?: ({
      text: string;
      as: 'primary' | 'secondary';
    } & (
      | {
          handler: () => void;
        }
      | {
          to: RouteLocationRaw;
        }
    ))[];
  };
  center?: boolean;
}>();
</script>

<template>
  <TmlAlertBox v-if="error" :type="error.level" :center="center">
    <template v-if="error.messages?.length">
      <template v-if="error.messages.length === 1">
        <template v-if="typeof error.messages[0] === 'string'">
          {{ error.messages[0] }}
        </template>
        <component :is="renderMessage(error.messages[0])" v-else />
      </template>
      <ul v-else class="list-disc pl-5 space-y-1">
        <li v-for="(message, index) in error.messages" :key="index">
          <template v-if="typeof message === 'string'">
            {{ message }}
          </template>
          <component :is="renderMessage(message)" v-else />
        </li>
      </ul>
    </template>
    <template v-else-if="typeof error.message === 'string'">
      {{ error.message }}
    </template>
    <component :is="renderMessage(error.message)" v-else />
    <template #actions>
      <slot name="actions" :error="error" :actions="error.actions || []">
        <TmlButton
          v-for="(action, index) in error.actions || []"
          :key="index"
          :to="'to' in action ? action.to : undefined"
          :as="action.as === 'primary' ? error.level : action.as"
          small
          class="-my-1 -mr-1 ml-2"
          @click="
            () => {
              if ('handler' in action) {
                action.handler();
              }
            }
          "
        >
          {{ action.text }}
        </TmlButton>
      </slot>
    </template>
  </TmlAlertBox>
</template>
